import { useEffect, useMemo, useCallback } from 'react';
import MojitoPresentation from 'mojito/presentation';
import SportsMenuViewTypes from '../types.js';
import { useDispatch } from 'react-redux';
import { actions as SportsMenuActions } from 'modules/sports-menu/store/slice.js';

const SortingHelper = MojitoPresentation.Utils.SortingHelper;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const { NavigationPathBuilder } = MojitoPresentation.Base.Navigation;
const { HideablePane, Image, RotatableImage, Button, Text, FlexPane, SportIcon } =
    MojitoPresentation.Components;
const { SPORT_TYPES } = SportsMenuViewTypes;

export default function SportsMenuView(props) {
    const { mojitoTools } = props;
    const { config, style, appContext, stringResolver } = mojitoTools;

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(SportsMenuActions.setFavoriteSportsMaxAmount(config.highlightedSports.maxAmount));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSportClicked = useCallback(
        (_event, data) => {
            appContext.analyticsEmitter.emitAnalytics('sportsMenuSportClicked', {
                sportType: data.sportId,
                applicationMode: appContext.systemSettings().applicationMode,
            });
            props.cbSportClicked();
            if (data.sportType === SPORT_TYPES.VIRTUAL) {
                IntentActionTrigger.showVirtualSportsPage();
            } else {
                IntentActionTrigger.showSport(data.sportId);
            }
        },
        [appContext, props]
    );

    const onFavoriteIconClicked = useCallback(
        (event, sportData) => {
            event.stopPropagation();
            event.preventDefault();
            dispatch(SportsMenuActions.toggleFavoriteSport(sportData.sportId));
        },
        [dispatch]
    );

    const getFilteredSports = useCallback(
        sports => {
            const hasName = sport => !!sport.name;
            const checkVirtuals = sport =>
                sport.type !== SPORT_TYPES.VIRTUAL || config.showVirtuals;
            return sports.filter(hasName).filter(checkVirtuals);
        },
        [config.showVirtuals]
    );

    const sortedSportList = useMemo(() => {
        const selectedLanguage = appContext.systemSettings().language;

        // Sort sports by name
        const sportNameSorter = (sportA, sportB) =>
            SortingHelper.compare(sportA.name, sportB.name, selectedLanguage);

        return getFilteredSports(props.availableSports).sort(sportNameSorter);
    }, [appContext, props.availableSports, getFilteredSports]);

    const handleToggleButtonClicked = () => {
        const isExpanded = !props.isExpanded;

        appContext.analyticsEmitter.emitAnalytics('sportsMenuExpandedStateChanged', isExpanded);

        dispatch(SportsMenuActions.showAllSports(isExpanded));
    };

    const renderSports = useCallback(
        sports => {
            return sports.map(sport => (
                <Sport
                    key={sport.id}
                    sport={sport}
                    onSportClicked={onSportClicked}
                    onFavoriteIconClicked={onFavoriteIconClicked}
                    mojitoTools={mojitoTools}
                    highlightedSports={props.highlightedSports}
                />
            ));
        },
        [props.highlightedSports, mojitoTools, onFavoriteIconClicked, onSportClicked]
    );

    const renderSeparator = () => {
        if (!config.separator.show) {
            return;
        }

        return <div style={style.separator} />;
    };

    // Renders the titles above the sport lists e.g. 'Top sports' and 'All sports'
    const renderTitle = title => {
        if (!config.listTitle.show) {
            return;
        }

        return (
            <Text config={config.style.listTitle} class="ta-SportsListTitle">
                {title}
            </Text>
        );
    };

    const renderSportItems = useCallback(() => {
        const renderExpandableSports = sports => {
            return (
                <HideablePane hidden={!props.isExpanded} config={config.allSportsContainer}>
                    {renderSports(sports)}
                </HideablePane>
            );
        };

        return !config.toggleButton.show
            ? renderSports(sortedSportList)
            : renderExpandableSports(sortedSportList);
    }, [config, props.isExpanded, renderSports, sortedSportList]);

    const highlightedSports = useMemo(() => {
        return props.highlightedSports
            .map(highlightedSport => {
                return sortedSportList.find(sport => sport.id === highlightedSport.id);
            })
            .filter(Boolean)
            .slice(0, config.highlightedSports.maxAmount);
    }, [sortedSportList, props.highlightedSports, config.highlightedSports.maxAmount]);

    let separator, highlightedSportsListTitle, highlightedSportItems;

    // Highlighted sports section should be rendered if it's configured to be shown and there are highlighted sports to be rendered
    if (
        props.showHighlightedSports &&
        config.highlightedSports.show &&
        highlightedSports &&
        highlightedSports.length > 0
    ) {
        separator = renderSeparator();
        highlightedSportsListTitle = renderTitle(
            stringResolver.resolveString('$SPORTS_MENU_VIEW.TOP_SPORT_TITLE')
        );
        highlightedSportItems = renderSports(highlightedSports);
    }

    return props.viewType === SportsMenuViewTypes.VIEW_TYPES.ONLY_HIGHLIGHTED ? (
        <FlexPane config={config.sportContainer} elementRef={props.elementRef}>
            {highlightedSportsListTitle}
            {highlightedSportItems}
        </FlexPane>
    ) : (
        <FlexPane config={config.container} elementRef={props.elementRef} class="ta-SportsMenu">
            <Header mojitoTools={mojitoTools} />
            <FlexPane config={config.sportContainer}>
                {highlightedSportsListTitle}
                {highlightedSportItems}
                {separator}
                {renderTitle(stringResolver.resolveString('$SPORTS_MENU_VIEW.ALL_SPORT_TITLE'))}
                <ToggleButton
                    mojitoTools={mojitoTools}
                    isExpanded={props.isExpanded}
                    handleToggleButtonClicked={handleToggleButtonClicked}
                />
                {renderSportItems()}
            </FlexPane>
        </FlexPane>
    );
}

SportsMenuView.getStyle = function (config, mode, merge) {
    return {
        separator: config.style.separator,
        expandedContainer: merge(config.style.toggleButton, config.style.expanded || {}),
    };
};

const Header = ({ mojitoTools }) => {
    const { config, stringResolver } = mojitoTools;

    const { header, headerText, headerIcon } = config.style;
    if (!config.header.show) {
        return;
    }
    const icon = headerIcon.src ? (
        <Image config={headerIcon} class="ta-SportsMenuHeaderIcon" />
    ) : null;
    return (
        <FlexPane config={header} class="ta-SportsMenuHeader">
            {icon}
            <Text config={headerText} class="ta-SportsMenuHeaderText">
                {stringResolver.resolveString('$SPORTS_MENU_VIEW.HEADER')}
            </Text>
        </FlexPane>
    );
};

const Sport = ({
    mojitoTools,
    sport,
    highlightedSports,
    onSportClicked,
    onFavoriteIconClicked,
}) => {
    const { config, appContext, stringResolver } = mojitoTools;
    const isFavoriteSport = highlightedSports.some(
        highlightedSport => highlightedSport.id === sport.id && highlightedSport.isFavoriteSport
    );

    const favoriteIcon = isFavoriteSport ? config.favoriteIconToggled : config.favoriteIcon;
    const routeResolver = appContext.routeResolver;
    const routingData = NavigationPathBuilder.toSport(sport.id);

    const sportName =
        sport.type === SPORT_TYPES.VIRTUAL
            ? stringResolver.resolveString('$VIRTUALS.TITLE')
            : sport.name;

    return (
        <Button
            hrefLink={routeResolver.getRoute(routingData)}
            key={sport.id}
            onClickData={{ sportId: sport.id, sportType: sport.type }}
            onClick={onSportClicked}
            config={config.sportItem}
            class={`ta-SportItem ta-${sport.id}`}
        >
            <SportIcon sportId={sport.id} config={config.sportIcon} alt={sportName} />
            <Text config={config.itemContainer} class="ta-SportName">
                {sportName}
            </Text>
            <Button
                onClick={onFavoriteIconClicked}
                onClickData={{ sportId: sport.id }}
                config={config.favoriteIconButton}
                class={`ta-favorite-icon ta-${sport.id}${isFavoriteSport ? ' ta-selected' : ''}`}
            >
                <Image config={favoriteIcon} class="ta-FavoriteIconImage" />
            </Button>
        </Button>
    );
};

const ToggleButton = ({ mojitoTools, isExpanded, handleToggleButtonClicked }) => {
    const { config, style, stringResolver } = mojitoTools;
    if (!config.toggleButton.show) {
        return null;
    }

    const seeAll = stringResolver.resolveString('$SPORTS_MENU_VIEW.SEE_ALL');
    const toggleButtonContainer = isExpanded ? style.expandedContainer : config.style.toggleButton;

    return (
        <Button
            class="ta-toggleButton"
            config={toggleButtonContainer}
            onClick={handleToggleButtonClicked}
        >
            <RotatableImage config={config.rotation} angle={isExpanded ? '0deg' : '180deg'} />
            <Text config={config.style.toggleText}>
                {isExpanded ? stringResolver.resolveString('$SPORTS_MENU_VIEW.SEE_LESS') : seeAll}
            </Text>
        </Button>
    );
};
